import { useEffect, useState } from "react";
import { settingsApi } from "./api";
import type { ImageUsageSnapshot } from "./types";
import "./image-usage-meter.css";

interface ImageUsageMeterProps {
  /** 隐藏的 sidebar 页面不读取用量，避免后台轮询。 */
  active: boolean;
  /** 入队成功后变更该值即可显式刷新。 */
  refreshKey?: number | string;
  className?: string;
}

function UsageBar({ label, used, limit, exempt }: { label: string; used: number; limit: number | null; exempt?: boolean }) {
  const maximum = limit === null ? Math.max(used, 1) : Math.max(limit, 1);
  const value = limit === null ? 0 : Math.min(used, maximum);
  const amount = exempt ? `${used} / 不限制（管理员）` : `${used} / ${limit === null ? "不限制" : limit}`;
  return <div className="image-usage-meter-row">
    <div className="image-usage-meter-label"><span>{label}</span><output>{amount}</output></div>
    <meter min={0} max={maximum} value={value} aria-label={`${label}：${amount}`} />
  </div>;
}

/** 只展示本会话可见范围内的实际计数；管理员个人限额豁免不伪装成普通 0/上限。 */
export function ImageUsageMeter({ active, refreshKey = 0, className = "" }: ImageUsageMeterProps) {
  const [usage, setUsage] = useState<ImageUsageSnapshot | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void settingsApi.loadImageUsage().then((snapshot) => {
      if (cancelled) return;
      setUsage(snapshot);
      setError(false);
    }).catch(() => {
      if (!cancelled) setError(true);
    });
    return () => { cancelled = true; };
  }, [active, refreshKey]);

  if (!active) return null;
  if (error) return <p className={`image-usage-meter is-error ${className}`} role="status">暂时无法读取今日用量。</p>;
  if (!usage) return <div className={`image-usage-meter is-loading ${className}`} aria-label="正在读取图片用量"><span /><span /></div>;
  return <div className={`image-usage-meter ${className}`} aria-label="图片今日用量">
    <UsageBar label="个人今日" used={usage.personal.used} limit={usage.personal.limit} exempt={usage.limit_exempt} />
    <UsageBar label="平台今日" used={usage.platform.used} limit={usage.platform.limit} />
  </div>;
}
